import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { firestore } from 'firebase';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Dispatch, bindActionCreators } from 'redux';
import * as Yup from 'yup';
import { IEmployee } from './@types/employee';
import { IGoogleLogInView } from './@types/model/auth/GoogleLogInView';
import { ILogInView } from './@types/model/auth/LogInView';
import { ISignUpView } from './@types/model/auth/SignUpView';
import { ISqlEmployee } from './@types/modelV2/rights/user/sqlEmployee';
import { IAuthState, IRootState, RootAction } from './@types/redux';
import { QUERY_DATA_STALE_TIME } from './appConstants';
import theme from './material';
import { IGoogleLoginError, IGoogleLoginSuccess } from './modules/customComponentV2/button/GoogleSignInButton';
import Login from './modules/customComponentV2/root/Login';
import Loader from './modules/customComponentV2/screen/CustomLoader';
import NavFrame from './modules/root/NavFrame';
import SnackbarNotifier from './modules/root/SnackbarNotifier';
import firebaseApp, { FirebaseService } from './services/firebaseService';
import EnvHelper from './services/helper/envHelper';
import AuthHttpService from './services/http/auth/authHttpService';
import UserHttpService from './services/right/userHttpService';
import { dispatch } from './store';
import AuthActions from './store/auth/actions';
import AuthFunctions from './store/auth/functions';
import GeneralFunctions from './store/general/functions';
import { navUrl } from './store/nav/actions';
import { VERSION } from './version';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import UserModelHelper from './@types/modelV2/rights/user/userModelHelper';
import AnimatedLoader from './modules/components/display/AnimatedLoader';

interface IAppProps extends RouteComponentProps {
    auth : IAuthState;
}

LicenseInfo.setLicenseKey(
    MUIXLICENCE,
);

const App = (props : IAppProps) : React.ReactElement => {

    const authObserver = async (firebaseUser : firebase.User | null) => {
        dispatch(AuthActions.authSetLoggingIn(true));

        if (firebaseUser) {
            try {
                await FirebaseService.initRemoteConfig();

                try { 
                    const sqlEmployee : ISqlEmployee = await UserHttpService.getUserByUid(firebaseUser.uid);

                    AuthFunctions.initialize(firebaseUser, UserModelHelper.fromSqlToFirebaseEmployee(sqlEmployee));
                } catch {
                    GeneralFunctions.generalShowErrorSnackbarV2('Error retrieving user.');
                    AuthFunctions.logoutUser();
                }
            } catch {
                AuthFunctions.logoutUser();
            } finally {
                dispatch(AuthActions.authSetLoggingIn(true));
            }
        } else {
            AuthFunctions.logoutUser();
        }

        dispatch(AuthActions.authSetLoggingIn(true));
    };

    useEffect(() => {
        dispatch(AuthActions.authSetLoggingIn(true));
        
        const unsubscribe = firebaseApp.auth().onAuthStateChanged(authObserver);

        Yup.addMethod(Yup.date, 'moment', function () {
            return this.transform((_currentValue, originalValue : moment.MomentInput | null | undefined) => {
                // If the original value is null:
                // - If the schema is nullable (e.g., Yup.date().nullable().moment()),
                //   and originalValue is null, it should pass as null.
                // - Otherwise, null or undefined should be treated as undefined by the transform,
                //   allowing Yup to handle it based on .required()/.optional().
                if (originalValue === null) {
                    // Check to see if the schema instance allows null.
                    return this.spec.nullable ? null : undefined;
                }
                if (originalValue === undefined) {
                    return undefined;
                }
                
                // Handle empty strings specifically, as moment('') can be problematic.
                // moment('') is invalid.
                if (typeof originalValue === 'string' && originalValue.trim() === '') {
                    return undefined; // Treat empty strings as invalid/undefined
                }

                const momentValue = moment(originalValue);

                if (momentValue.isValid()) {
                    return momentValue.utc().toDate();
                } else {
                    // If parsing fails, return undefined to let Yup mark it as invalid.
                    return undefined;
                }
            });
        });

        return () => {
            unsubscribe();
        };
    }, []);

    //  useEffect(() => {
    //     authFunctions.setAuthState();

    //     Yup.addMethod(Yup.date, 'moment', () => {
    //         return Yup.date().transform((value : moment.MomentInput | null) => {
    //             if (Yup.date().isType(value)) return value;
          
    //             if (!value) return null;

    //             const momentValue = moment(value);
    //             return momentValue.isValid() ? momentValue.utc().toDate() : null;
    //         }).nullable();
    //     });
    // }, []);

    const manualLogIn = async (emailOrUsername : string, password : string) : Promise<void> => {
        try {
            dispatch(AuthActions.authSetLoggingIn(true));

            const loginView : ILogInView = {
                emailOrUsername,
                password,
            };
        
            const token = await AuthHttpService.logInManual(loginView);

            await firebaseApp.auth().signInWithCustomToken(token);

            navUrl('/');
        } catch (e) {
            GeneralFunctions.generalShowErrorSnackbarV2('An error occurred while logging in.', e);
        } finally {
            dispatch(AuthActions.authSetLoggingIn(false));
        }
    };

    const manualSignUp = async (employeeNumber : string, password : string) : Promise<void> => {
        try {
            dispatch(AuthActions.authSetLoggingIn(true));

            const signUpView : ISignUpView = {
                employeeNumber,
                password,
            };

            const token = await AuthHttpService.signUp(signUpView);

            await firebaseApp.auth().signInWithCustomToken(token);

            navUrl('/');
        } catch (e) {
            GeneralFunctions.generalShowErrorSnackbarV2('An error occurred while logging in.', e);
        } finally {
            dispatch(AuthActions.authSetLoggingIn(false));
        }
    };

    const onGoogleLogInSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        try {
            dispatch(AuthActions.authSetLoggingIn(true));

            const googleLogInView : IGoogleLogInView = {
                code: response.code,
                primaryImei: null,
            };

            const token = await AuthHttpService.googleLogIn(googleLogInView);

            await firebaseApp.auth().signInWithCustomToken(token);

            navUrl('/');
        } catch (e) {
            GeneralFunctions.generalShowErrorSnackbarV2('An error occurred while logging in.', e);
        } finally {
            dispatch(AuthActions.authSetLoggingIn(false));
        }
    };

    const onGoogleLogInFailure = (response : IGoogleLoginError) : void => {
        GeneralFunctions.generalShowErrorSnackbarV2('An error occurred while logging in. ' + response.error);
        dispatch(AuthActions.authSetLoggingIn(false));
    };

    const { auth } = props;

    const renderApp = useMemo(() => {
        if (auth.isLoggingIn) {
            return <AnimatedLoader />;
        }
        
        if (auth.session) {
            return (
                <NavFrame />
            );
        }

        return (<Login
            isLoading={auth.isLoggingIn}
            env={EnvHelper.getEnvName()}
            version={VERSION.version}
            isLoggingIn={auth.isLoggingIn}
            manualLogIn={manualLogIn}
            onGoogleSignInSuccess={onGoogleLogInSuccess}
            onGoogleSignInFailure={onGoogleLogInFailure}
            enableSignUp={true}
            manualSignUp={manualSignUp}
        />);

    }, [
        auth.isLoggingIn,
        auth.session,
    ]);

        const queryClient = new QueryClient({
        defaultOptions: {
            queries: { 
                staleTime: QUERY_DATA_STALE_TIME,
                retry: 3,
                retryDelay: 15000,
            },
            mutations: {}
        }
    });

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
                {renderApp}
                <SnackbarNotifier/>
            </ThemeProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
    };
};

const mapDispatchToProps = (dispatcher : Dispatch<RootAction>) => bindActionCreators(
    { }, dispatcher);

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(App));
